import { useState, useMemo, useCallback, type Dispatch, type SetStateAction, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getStageConfig } from "@/data/workflowEngine";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ClipboardCheck, Eye, Camera, Ruler, RotateCcw, Send, FileSignature, AlertTriangle, Search,
} from "lucide-react";
import type { Issue, Job } from "@/data/types";
import type { BuildReviewEvent, BuildReviewStatus, RevisionReason } from "@/data/buildReviewTypes";
import { deriveBuildReviewStatus, REVISION_REASON_LABELS } from "@/data/buildReviewTypes";
import { IntakeReviewWorkspace } from "@/components/IntakeReviewWorkspace";

import { RescheduleModal } from "@/components/quick-actions/RescheduleModal";
import { QuickAssignModal } from "@/components/quick-actions/QuickAssignModal";
import { QuickResolveIssueModal } from "@/components/quick-actions/QuickResolveIssueModal";
import { PermitQuickViewModal } from "@/components/quick-actions/PermitQuickViewModal";
import { usePushChain } from "@/hooks/usePushChain";
import { BuildReviewService } from "@/services";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppData } from "@/data/AppDataContext";
import { BuildReviewSkeleton } from "@/components/ui/build-review-skeleton";
import { CockpitFilterBar, CockpitSearch } from "@/components/cockpit/CockpitShell";

const DISPLAY_PROJECT_TYPE = (pt: string) =>
  pt === "Tub to Shower Conversion" ? "Shower Conversion" : pt;

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function getJobDisplayRef(job: Pick<Job, "id" | "jobNumber">): string {
  const jobNumber = String(job.jobNumber ?? "").trim();
  if (jobNumber) return jobNumber;
  const fallback = String(job.id ?? "").trim();
  if (!fallback) return "JOB-UNASSIGNED";
  return isUuidLike(fallback) ? `JOB ${fallback.slice(0, 8)}` : fallback;
}

function getProductLabel(job: Job): string {
  const material = job.projectConfig?.bathroomMaterials?.[0] ?? job.projectConfig?.primaryMaterial ?? "";
  return [material, DISPLAY_PROJECT_TYPE(job.projectType)].filter(Boolean).join(" ");
}

type ReviewLane = "needs-review" | "on-hold-sales" | "revision-required" | "awaiting-sales" | "awaiting-signature" | "resubmitted" | "final-review";

const LANE_CONFIG: Record<ReviewLane, { label: string; icon: React.ReactNode; color: string; borderColor: string; bgColor: string; badgeColor: string }> = {
  "needs-review":       { label: "Build Review Required",           icon: <Eye className="h-4 w-4 text-warning" />,            color: "text-warning",          borderColor: "border-warning/20",     bgColor: "bg-warning/[0.03]",    badgeColor: "bg-warning/15 text-warning" },
  "on-hold-sales":      { label: "On Hold — Sales Action Required", icon: <AlertTriangle className="h-4 w-4 text-destructive" />, color: "text-destructive",   borderColor: "border-destructive/20", bgColor: "bg-destructive/[0.03]", badgeColor: "bg-destructive/15 text-destructive" },
  "revision-required":  { label: "Revision Required",              icon: <RotateCcw className="h-4 w-4 text-destructive" />,  color: "text-destructive",      borderColor: "border-destructive/20", bgColor: "bg-destructive/[0.03]", badgeColor: "bg-destructive/15 text-destructive" },
  "awaiting-sales":     { label: "Awaiting Sales Confirmation",    icon: <Send className="h-4 w-4 text-primary" />,           color: "text-primary",          borderColor: "border-primary/20",     bgColor: "bg-primary/[0.03]",    badgeColor: "bg-primary/15 text-primary" },
  "awaiting-signature": { label: "Awaiting Customer Signature",    icon: <FileSignature className="h-4 w-4 text-primary" />,  color: "text-primary",          borderColor: "border-primary/20",     bgColor: "bg-primary/[0.03]",    badgeColor: "bg-primary/15 text-primary" },
  "resubmitted":        { label: "Resubmitted for Review",         icon: <RotateCcw className="h-4 w-4 text-primary" />,      color: "text-primary",          borderColor: "border-primary/20",     bgColor: "bg-primary/[0.03]",    badgeColor: "bg-primary/15 text-primary" },
  "final-review":       { label: "Final Build Review",              icon: <ClipboardCheck className="h-4 w-4 text-success" />, color: "text-success",          borderColor: "border-success/20",     bgColor: "bg-success/[0.03]",    badgeColor: "bg-success/15 text-success" },
};

// ─── Aging buckets — anchored to right-of-rescission (3 business days) ───
// Target: same-day. Acceptable: by Day 2. At-risk: Day 3 (last day before
// rescission window closes). Overdue: past 72h — rescission risk.
type AgeBucket = "overdue" | "at-risk" | "watch" | "fresh";

const BUCKET_META: Record<AgeBucket, { label: string; sub: string; tone: string; ring: string; bar: string; pill: string }> = {
  overdue: {
    label: "Past Rescission Window",
    sub: ">72h — escalate, customer can still cancel",
    tone: "text-destructive",
    ring: "border-destructive/30 bg-destructive/[0.04]",
    bar: "bg-destructive",
    pill: "bg-destructive/15 text-destructive",
  },
  "at-risk": {
    label: "At Risk",
    sub: "48–72h — final business day before rescission",
    tone: "text-warning",
    ring: "border-warning/30 bg-warning/[0.04]",
    bar: "bg-warning",
    pill: "bg-warning/15 text-warning",
  },
  watch: {
    label: "Watch",
    sub: "24–48h — clear today",
    tone: "text-primary",
    ring: "border-primary/20 bg-primary/[0.03]",
    bar: "bg-primary",
    pill: "bg-primary/15 text-primary",
  },
  fresh: {
    label: "Same Day",
    sub: "<24h — on target",
    tone: "text-muted-foreground",
    ring: "border-border bg-card",
    bar: "bg-muted-foreground/30",
    pill: "bg-muted text-muted-foreground",
  },
};

const BUCKET_ORDER: AgeBucket[] = ["overdue", "at-risk", "watch", "fresh"];

// Severity rank for tiebreaker within a bucket (higher = more urgent)
const LANE_SEVERITY: Record<ReviewLane, number> = {
  "on-hold-sales": 6,
  "revision-required": 5,
  "resubmitted": 4,
  "needs-review": 3,
  "awaiting-sales": 2,
  "awaiting-signature": 1,
  "final-review": 0,
};

const STATUS_TO_LANE: Record<BuildReviewStatus, ReviewLane> = {
  "Needs Review": "needs-review",
  "On Hold — Sales Action Required": "on-hold-sales",
  "Revision Required": "revision-required",
  "Awaiting Sales Confirmation": "awaiting-sales",
  "Awaiting Customer Signature": "awaiting-signature",
  "Resubmitted for Review": "resubmitted",
  "Final Review": "final-review",
  "Approved": "final-review",
};

interface BuildReviewPanelProps {
  jobs?: Job[];
  issues?: Issue[];
  setJobs?: Dispatch<SetStateAction<Job[]>>;
  buildReviewEvents?: BuildReviewEvent[];
  totalCount?: number;
  bucketCounts?: Partial<Record<AgeBucket, number>>;
  activeBucket?: AgeBucket | "all";
  onBucketChange?: (bucket: AgeBucket | "all") => void;
  isLoading?: boolean;
  isError?: boolean;
  onRefresh?: () => Promise<void> | void;
}

/**
 * Resolve when this job entered its current review-pending state.
 * Priority:
 *   1. Most recent BuildReviewEvent (authoritative — actual review activity).
 *   2. Most recent activityHistory entry (proxy: last touch on the job).
 *   3. Now (no signal — treat as just entered).
 */
function getReviewEnteredAt(job: Job, events: BuildReviewEvent[]): number {
  if (events.length > 0) {
    return new Date(events[events.length - 1].timestamp).getTime();
  }
  const history = job.activityHistory ?? [];
  if (history.length > 0) {
    const last = history[history.length - 1]?.date;
    if (last) return new Date(last).getTime();
  }
  return Date.now();
}

function getAgeBucket(ageHours: number): AgeBucket {
  if (ageHours >= 72) return "overdue";
  if (ageHours >= 48) return "at-risk";
  if (ageHours >= 24) return "watch";
  return "fresh";
}

function formatAge(hours: number): string {
  if (hours >= 24) {
    const d = Math.floor(hours / 24);
    const h = Math.floor(hours - d * 24);
    return h > 0 ? `${d}d ${h}h` : `${d}d`;
  }
  if (hours >= 1) return `${Math.floor(hours)}h`;
  return `${Math.max(1, Math.floor(hours * 60))}m`;
}

export function BuildReviewPanel({
  jobs: jobsOverride,
  issues: issuesOverride,
  setJobs: setJobsOverride,
  buildReviewEvents: buildReviewEventsOverride,
  totalCount: totalCountOverride,
  bucketCounts: bucketCountsOverride,
  activeBucket = "all",
  onBucketChange,
  isLoading: isLoadingOverride,
  isError: isErrorOverride,
  onRefresh,
}: BuildReviewPanelProps = {}) {
  const appData = useAppData();
  const jobs = jobsOverride ?? appData.jobs;
  const issues = issuesOverride ?? appData.issues;
  const setJobs = setJobsOverride ?? appData.setJobs;
  const navigate = useNavigate();

  const [reviewJobId, setReviewJobId] = useState<string | null>(null);
  const [reviewJobLane, setReviewJobLane] = useState<ReviewLane>("needs-review");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [laneFilter, setLaneFilter] = useState<ReviewLane | "all">("all");

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const findOpenIssueForJob = (jobId: string) =>
    issues.find(i => i.jobId === jobId && i.status !== "Resolved" && i.status !== "Closed") ?? null;

  const pushChain = usePushChain({ navigate, findOpenIssueForJob });

  // Fetch all build review events from Supabase
  const queryClient = useQueryClient();
  const buildReviewQuery = useQuery({
    queryKey: ["build-review", "all-events"],
    queryFn: async () => {
      const { data, error } = await BuildReviewService.getAll();
      if (error) throw error;
      return data ?? [];
    },
    enabled: !buildReviewEventsOverride,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
  });

  // Wrap in useMemo to satisfy lint rule about dependencies
  const buildReviewEvents = useMemo(
    () => buildReviewEventsOverride ?? buildReviewQuery.data ?? [],
    [buildReviewEventsOverride, buildReviewQuery.data],
  );
  const isReviewLoading = isLoadingOverride ?? (!buildReviewEventsOverride && buildReviewQuery.isLoading);
  const isReviewError = isErrorOverride ?? (!buildReviewEventsOverride && buildReviewQuery.isError);

  const refreshReviewData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["build-review", "all-events"] });
    await onRefresh?.();
  }, [onRefresh, queryClient]);

  const filteredJobs = useMemo(() => {
    let result = jobs.filter(j => (getStageConfig(j.currentStage)?.order ?? 0) < 100);
    if (laneFilter !== "all") {
      result = result.filter(job => {
        const evts = buildReviewEvents.filter(e => e.jobId === job.id);
        const status = deriveBuildReviewStatus(evts);
        return STATUS_TO_LANE[status] === laneFilter;
      });
    }
    if (debouncedSearch.trim()) {
      const s = debouncedSearch.trim().toLowerCase();
      result = result.filter(job =>
        String(job.customerName ?? "").toLowerCase().includes(s) ||
        String(job.address?.city ?? "").toLowerCase().includes(s) ||
        String(job.jobNumber ?? "").toLowerCase().includes(s) ||
        getJobDisplayRef(job).toLowerCase().includes(s) ||
        getProductLabel(job).toLowerCase().includes(s)
      );
    }
    return result;
  }, [jobs, buildReviewEvents, laneFilter, debouncedSearch]);

  // Derive status for each job (only approved jobs are excluded here)
  const jobReviewMap = useMemo(() => {
    const map = new Map<string, { job: Job; status: BuildReviewStatus; events: BuildReviewEvent[] }>();
    filteredJobs.forEach(job => {
      const evts = buildReviewEvents.filter(e => e.jobId === job.id);
      const status = deriveBuildReviewStatus(evts);
      if (status !== "Approved") {
        map.set(job.id, { job, status, events: evts });
      }
    });
    return map;
  }, [filteredJobs, buildReviewEvents]);

  // Compute aging-bucketed entries (canonical hierarchy: SLA-first, severity tiebreaker)
  const NOW = Date.now();
  type Entry = {
    job: Job;
    status: BuildReviewStatus;
    events: BuildReviewEvent[];
    lane: ReviewLane;
    ageHours: number;
    bucket: AgeBucket;
  };

  const buckets = useMemo(() => {
    const result: Record<AgeBucket, Entry[]> = { overdue: [], "at-risk": [], watch: [], fresh: [] };
    jobReviewMap.forEach(({ job, status, events }) => {
      const lane = STATUS_TO_LANE[status];
      const enteredAt = getReviewEnteredAt(job, events);
      const ageHours = Math.max(0, (NOW - enteredAt) / 36e5);
      const bucket = getAgeBucket(ageHours);
      result[bucket].push({ job, status, events, lane, ageHours, bucket });
    });
    BUCKET_ORDER.forEach(b => {
      result[b].sort((a, z) => z.ageHours - a.ageHours || LANE_SEVERITY[z.lane] - LANE_SEVERITY[a.lane]);
    });
    return result;
  }, [jobReviewMap, NOW]);

  const currentPageCount = Array.from(jobReviewMap.values()).length;
  const totalCount = totalCountOverride ?? currentPageCount;
  const allBucketCount = bucketCountsOverride
    ? BUCKET_ORDER.reduce((sum, bucket) => sum + (bucketCountsOverride[bucket] ?? 0), 0)
    : currentPageCount;

  const handleApprove = useCallback(async (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    
    try {
      // Use the service's approve method which handles both event creation and job updates
      const { data, error } = await BuildReviewService.approve(job.jobNumber, "ops_manager");
      if (error) throw error;
      
      // Update local jobs state optimistically
      setJobs(prev => prev.map(j =>
        j.id === jobId
          ? {
              ...j,
              scopeLocked: true,
              intakeApproved: true,
              approvedForOrdering: true,
              planningStatus: "Ready for Ordering" as const,
              orderStatus: "Not Ordered",
              procurementStatus: "Not Ordered",
              materialReadiness: "Not Ordered",
              currentStage: "Review Complete" as const,
              activityHistory: [...j.activityHistory, {
                id: `act-${Date.now()}`,
                date: new Date().toISOString(),
                action: "Build review approved — moved to Planning",
                user: "Ops Manager",
                noteType: "ops" as const,
              }],
            }
          : j
      ));
      await refreshReviewData();
      
      toast.success("Build review approved", { description: "Job moved to Material Planning" });
    } catch (error) {
      console.error("Failed to approve build review:", error);
      toast.error("Failed to approve build review");
    }
  }, [jobs, setJobs, refreshReviewData]);

  const handleRevisionFromWorkspace = useCallback(async (jobId: string, notes: string, materials: string, reason: RevisionReason) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    
    try {
      // Use the service's requestRevision method
      const { data, error } = await BuildReviewService.requestRevision(
        job.jobNumber,
        "ops_manager",
        notes,
        reason,
        materials
      );
      if (error) throw error;
      
      // Update local jobs state optimistically
      setJobs(prev => prev.map(j =>
        j.id === jobId
          ? { ...j, holdStatus: "On Hold — Sales Action Required", holdReason: REVISION_REASON_LABELS[reason] }
          : j
      ));
      await refreshReviewData();
      
      toast.info("Revision requested", {
        description: `On Hold — assigned to ${job.salesRep || "Sales Rep"} for resolution`,
      });
      setReviewJobId(null);
    } catch (error) {
      console.error("Failed to request revision:", error);
      toast.error("Failed to request revision");
    }
  }, [jobs, setJobs, refreshReviewData]);

  // Other handlers...

  const reviewJob = reviewJobId ? jobs.find(j => j.id === reviewJobId) : null;

  if (isReviewLoading) {
    return <BuildReviewSkeleton />;
  }

  if (isReviewError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2.5">
          <ClipboardCheck className="h-5 w-5 text-warning" />
          <h2 className="text-[16px] font-bold tracking-tight text-foreground">Build Review & Scope Validation</h2>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          Failed to load build review data
        </div>
      </div>
    );
  }

  if (allBucketCount === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2.5">
          <ClipboardCheck className="h-5 w-5 text-warning" />
          <h2 className="text-[16px] font-bold tracking-tight text-foreground">Build Review & Scope Validation</h2>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          No jobs pending build review at this time
        </div>
      </div>
    );
  }

  const bucketCounts: Record<AgeBucket, number> = {
    overdue: bucketCountsOverride?.overdue ?? buckets.overdue.length,
    "at-risk": bucketCountsOverride?.["at-risk"] ?? buckets["at-risk"].length,
    watch: bucketCountsOverride?.watch ?? buckets.watch.length,
    fresh: bucketCountsOverride?.fresh ?? buckets.fresh.length,
  };
  const selectedBucketEmptyMessage = activeBucket === "all"
    ? "No jobs on this page."
    : `No ${BUCKET_META[activeBucket].label.toLowerCase()} jobs pending build review.`;

  return (
    <div className="space-y-4">
      {/* Section Header with SLA summary chips */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <ClipboardCheck className="h-5 w-5 text-warning" />
          <h2 className="text-[16px] font-bold tracking-tight text-foreground">Build Review & Scope Validation</h2>
          <span className="text-[11px] font-medium text-muted-foreground">
            {activeBucket === "all"
              ? `${allBucketCount} pending`
              : `${totalCount} ${BUCKET_META[activeBucket].label} pending`} · target same-day · max next-day
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {onBucketChange && (
            <button
              type="button"
              onClick={() => onBucketChange("all")}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                activeBucket === "all"
                  ? "bg-foreground text-background"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              All
              <span className="font-bold tabular-nums">{allBucketCount}</span>
            </button>
          )}
          {BUCKET_ORDER.map(b => {
            const meta = BUCKET_META[b];
            const count = bucketCounts[b];
            const active = activeBucket === b;
            const className = `inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${meta.pill} ${
              count === 0 && !active ? "opacity-40" : ""
            } ${onBucketChange ? "hover:opacity-80" : ""} ${active ? "ring-1 ring-current" : ""}`;
            const content = (
              <>
                <span className={`h-1.5 w-1.5 rounded-full ${meta.bar}`} />
                {meta.label}
                <span className="font-bold tabular-nums">{count}</span>
              </>
            );
            if (onBucketChange) {
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => onBucketChange(b)}
                  className={className}
                  aria-pressed={active}
                >
                  {content}
                </button>
              );
            }
            return (
              <span
                key={b}
                className={className}
              >
                {content}
              </span>
            );
          })}
        </div>
      </div>

      <CockpitFilterBar
        search={
          <CockpitSearch
            value={search}
            onChange={setSearch}
            placeholder="Search jobs, customers, cities…"
          />
        }
        right={
          <Select value={laneFilter} onValueChange={(v) => setLaneFilter(v as ReviewLane | "all")}>
            <SelectTrigger className="w-[200px] h-9">
              <SelectValue placeholder="All Lanes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Lanes</SelectItem>
              {Object.entries(LANE_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {/* Aging Buckets — primary hierarchy */}
      {BUCKET_ORDER.map(bucket => {
        const items = buckets[bucket];
        if (items.length === 0) return null;
        const meta = BUCKET_META[bucket];

        return (
          <div key={bucket} className="space-y-2">
            <div className="flex items-baseline gap-2 px-0.5">
              <div className={`h-2 w-2 rounded-full ${meta.bar}`} />
              <h3 className={`text-[13px] font-bold uppercase tracking-[0.08em] ${meta.tone}`}>{meta.label}</h3>
              <span className="text-[11px] text-muted-foreground/60 font-medium">
                {items.length} job{items.length !== 1 ? "s" : ""} · {meta.sub}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {items.map(({ job, status, events, lane, ageHours }) => {
                const cfg = LANE_CONFIG[lane];
                return (
                  <Card
                    key={job.id}
                    className={`relative overflow-hidden border ${meta.ring} shadow-none transition-all hover:-translate-y-[1px] hover:shadow-md`}
                  >
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${meta.bar}`} />
                    <CardContent className="px-4 py-3 pl-5 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[13px] font-bold text-foreground truncate">
                          {job.customerName} — {job.address.city || "Unknown"}
                        </p>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded ${meta.pill}`}>
                            {formatAge(ageHours)}
                          </span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.badgeColor}`}>
                            {status}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-[12px] text-muted-foreground">{getProductLabel(job)}</span>
                        <div className="flex items-center gap-2 ml-auto">
                          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/60">
                            <Camera className="h-3 w-3" />
                            {job.photos?.length || 0}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/60">
                            <Ruler className="h-3 w-3" />
                            {job.projectConfig?.totalSqFt ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>

                      {(lane === "on-hold-sales" || lane === "revision-required") && events.length > 0 && (() => {
                        const revEvt = events.filter(e => e.action === "revision_requested").pop();
                        return revEvt ? (
                          <div className="space-y-1">
                            {revEvt.revisionReason && (
                              <p className="text-[10px] font-semibold text-destructive/80 bg-destructive/5 rounded px-2 py-0.5">
                                Reason: {REVISION_REASON_LABELS[revEvt.revisionReason]}
                              </p>
                            )}
                            <p className="text-[11px] text-destructive/80 bg-destructive/5 rounded px-2 py-1">
                              {revEvt.revisionNotes || "Revision details pending"}
                            </p>
                          </div>
                        ) : null;
                      })()}

                      <div className="flex items-center gap-2 pt-1">
                        <Button
                          size="sm"
                          variant={bucket === "overdue" || bucket === "at-risk" ? "default" : "outline"}
                          className="h-7 text-[11px] font-semibold gap-1"
                          onClick={() => { setReviewJobId(job.id); setReviewJobLane(lane); }}
                        >
                          <Eye className="h-3 w-3" /> Review Project
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {currentPageCount === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 py-8 text-center text-sm text-muted-foreground">
          {selectedBucketEmptyMessage}
        </div>
      )}

      {/* ═══ FULL-SCREEN REVIEW WORKSPACE ═══ */}
      <IntakeReviewWorkspace
        job={reviewJob ?? null}
        open={!!reviewJobId}
        lane={reviewJobLane}
        buildReviewEvents={buildReviewEvents}
        openIssues={issues}
        onClose={() => setReviewJobId(null)}
        onApprove={(jobId) => {
          handleApprove(jobId);
          setReviewJobId(null);
        }}
        onApproveAndContinue={(jobId) => {
          // Commit approval state but keep workspace open so the next-step
          // panel can render with an optimistic post-approval snapshot.
          handleApprove(jobId);
        }}
        onContinueChain={(approvedJob, step) => {
          setReviewJobId(null);
          if (step.kind === "done") return;
          if (step.kind === "generate_po") {
            toast.info("Build Review approved", {
              description: "Head to the Financials tab to manage ordering.",
            });
            return;
          }
          if (step.kind === "navigate")      { navigate(step.to); return; }
          // Keep operator in Push Mode for the remaining modal kinds.
          pushChain.chainTo(step, approvedJob);
        }}
        onRequestRevision={handleRevisionFromWorkspace}
        onSendToSales={async (jobId) => {
          const job = jobs.find(j => j.id === jobId);
          if (!job) return;

          try {
            const { error } = await BuildReviewService.sendToSales(job.jobNumber, "ops_manager");
            if (error) throw error;
            await refreshReviewData();
            toast.info("Sent to Sales", { description: "Awaiting sales confirmation" });
            setReviewJobId(null);
          } catch (error) {
            console.error("Failed to send to sales:", error);
            toast.error("Failed to send to sales");
          }
        }}
        onSalesApprove={async (jobId) => {
          const job = jobs.find(j => j.id === jobId);
          if (!job) return;

          try {
            const { error } = await BuildReviewService.salesConfirm(job.jobNumber, "ops_manager");
            if (error) throw error;
            await refreshReviewData();
            toast.success("Sales approved", { description: "Change order created — awaiting customer signature" });
            setReviewJobId(null);
          } catch (error) {
            console.error("Failed to confirm sales:", error);
            toast.error("Failed to confirm sales");
          }
        }}
        onCustomerSigned={async (jobId) => {
          const job = jobs.find(j => j.id === jobId);
          if (!job) return;

          try {
            const { error } = await BuildReviewService.customerSigned(job.jobNumber, "ops_manager");
            if (error) throw error;
            await refreshReviewData();
            toast.success("Customer signed", { description: "Moved to Final Build Review" });
            setReviewJobId(null);
          } catch (error) {
            console.error("Failed to record customer signature:", error);
            toast.error("Failed to record customer signature");
          }
        }}
        onFinalApproval={async (jobId) => {
          const job = jobs.find(j => j.id === jobId);
          if (!job) return;

          try {
            const { error } = await BuildReviewService.finalApprove(job.jobNumber, "ops_manager");
            if (error) throw error;

            setJobs(prev => prev.map(j =>
              j.id === jobId
                ? {
                    ...j,
                    scopeLocked: true,
                    intakeApproved: true,
                    approvedForOrdering: true,
                    planningStatus: "Ready for Ordering" as const,
                    currentStage: "Review Complete" as const,
                    holdStatus: undefined,
                    holdReason: undefined,
                  }
                : j
            ));
            await refreshReviewData();

            toast.success("Final review approved", { description: "Job cleared for Material Planning" });
            setReviewJobId(null);
          } catch (error) {
            console.error("Failed to final approve:", error);
            toast.error("Failed to final approve");
          }
        }}
      />


      {/* ═══ PUSH-MODE CHAINED MODALS ═══ */}
      {pushChain.reschedule && (
        <RescheduleModal
          open={!!pushChain.reschedule}
          onOpenChange={(o) => !o && pushChain.closeAll()}
          job={pushChain.reschedule}
          openIssues={issues.filter(i => i.jobId === pushChain.reschedule!.id)}
          onChooseNextStep={(step) => pushChain.chainTo(step, pushChain.reschedule!)}
        />
      )}
      {pushChain.assign && (
        <QuickAssignModal
          open={!!pushChain.assign}
          onOpenChange={(o) => !o && pushChain.closeAll()}
          job={pushChain.assign}
          openIssues={issues.filter(i => i.jobId === pushChain.assign!.id)}
          onChooseNextStep={(step) => pushChain.chainTo(step, pushChain.assign!)}
        />
      )}
      {pushChain.permit && (
        <PermitQuickViewModal
          open={!!pushChain.permit}
          onOpenChange={(o) => !o && pushChain.closeAll()}
          job={pushChain.permit}
          onChooseNextStep={(step) => pushChain.chainTo(step, pushChain.permit!)}
        />
      )}
      {pushChain.resolve && (
        <QuickResolveIssueModal
          open={!!pushChain.resolve}
          onOpenChange={(o) => !o && pushChain.closeAll()}
          issue={pushChain.resolve}
          onChooseNextStep={(step) => {
            const job = jobs.find(j => j.id === pushChain.resolve!.jobId);
            if (job) pushChain.chainTo(step, job);
          }}
        />
      )}
    </div>
  );
}
